import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Chip,
  Paper,
  TextField,
  Alert,
  Divider,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PlayArrow as TestIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
} from 'recharts';
import { agentsApi, testRunsApi } from '../services/api';
import { exportAgentPerformancePdf } from '../utils/exportPdf';

function AgentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [testHistory, setTestHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Filter state
  const [allTestRuns, setAllTestRuns] = useState([]);
  const [selectedTestRunIds, setSelectedTestRunIds] = useState([]);
  const [loadingTestRuns, setLoadingTestRuns] = useState(false);

  useEffect(() => {
    loadAgent();
    loadTestHistory();
    loadAllTestRuns();
  }, [id]);

  const loadAgent = async () => {
    try {
      const response = await agentsApi.get(id);
      setAgent(response.data);
      setFormData({
        name: response.data.name,
        description: response.data.description || '',
        endpoint_url: response.data.endpoint_url,
        api_key: response.data.api_key || '',
        api_key_header: response.data.api_key_header || 'Authorization',
        payload_template: JSON.stringify(response.data.payload_template, null, 2),
        response_path: response.data.response_path,
        headers: JSON.stringify(response.data.headers || {}, null, 2),
        is_active: response.data.is_active,
      });
    } catch (error) {
      console.error('Error loading agent:', error);
      setError('Erro ao carregar agente');
    } finally {
      setLoading(false);
    }
  };

  const loadAllTestRuns = async () => {
    setLoadingTestRuns(true);
    try {
      const response = await testRunsApi.list();
      // Filter only completed test runs for this agent
      const agentTestRuns = response.data.filter(
        (tr) => tr.agent_id === parseInt(id) && tr.status === 'completed'
      );
      setAllTestRuns(agentTestRuns);
    } catch (error) {
      console.error('Error loading test runs:', error);
    } finally {
      setLoadingTestRuns(false);
    }
  };

  const loadTestHistory = useCallback(async (filterIds) => {
    setLoadingHistory(true);
    try {
      const idsToFilter = filterIds !== undefined ? filterIds : selectedTestRunIds;
      const response = await agentsApi.getTestHistory(id, idsToFilter);
      setTestHistory(response.data);
    } catch (error) {
      console.error('Error loading test history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [id, selectedTestRunIds]);

  const handleFilterChange = (event) => {
    const value = event.target.value;
    // On autofill we get a stringified value.
    const newIds = typeof value === 'string' ? value.split(',').map(Number) : value;
    setSelectedTestRunIds(newIds);
    loadTestHistory(newIds);
  };

  const handleClearFilter = () => {
    setSelectedTestRunIds([]);
    loadTestHistory([]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      let payloadTemplate, headers;
      
      try {
        payloadTemplate = JSON.parse(formData.payload_template);
      } catch {
        setError('Payload template inválido');
        setSaving(false);
        return;
      }
      
      try {
        headers = JSON.parse(formData.headers);
      } catch {
        setError('Headers inválidos');
        setSaving(false);
        return;
      }
      
      await agentsApi.update(id, {
        name: formData.name,
        description: formData.description,
        endpoint_url: formData.endpoint_url,
        api_key: formData.api_key,
        api_key_header: formData.api_key_header,
        payload_template: payloadTemplate,
        response_path: formData.response_path,
        headers: headers,
        is_active: formData.is_active,
      });
      
      setEditing(false);
      loadAgent();
    } catch (error) {
      console.error('Error saving agent:', error);
      setError('Erro ao salvar agente');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await agentsApi.testConnection(id);
      setTestResult(response.data);
    } catch (error) {
      setTestResult({ success: false, message: 'Erro ao testar conexão' });
    } finally {
      setTesting(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      name: agent.name,
      description: agent.description || '',
      endpoint_url: agent.endpoint_url,
      api_key: agent.api_key || '',
      api_key_header: agent.api_key_header || 'Authorization',
      payload_template: JSON.stringify(agent.payload_template, null, 2),
      response_path: agent.response_path,
      headers: JSON.stringify(agent.headers || {}, null, 2),
      is_active: agent.is_active,
    });
  };

  const handleExportPdf = () => {
    if (!testHistory || !agent) return;
    const filterLabel = selectedTestRunIds.length > 0
      ? allTestRuns
          .filter((tr) => selectedTestRunIds.includes(tr.id))
          .map((tr) => tr.name)
          .join(', ')
      : 'Todos os testes';
    exportAgentPerformancePdf(agent, testHistory, filterLabel);
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUpIcon color="success" />;
      case 'declining':
        return <TrendingDownIcon color="error" />;
      default:
        return <TrendingFlatIcon color="action" />;
    }
  };

  const getTrendLabel = (trend) => {
    switch (trend) {
      case 'improving':
        return 'Melhorando';
      case 'declining':
        return 'Piorando';
      case 'stable':
        return 'Estável';
      default:
        return 'Sem dados';
    }
  };

  const formatChartData = () => {
    if (!testHistory?.history) return [];
    return testHistory.history.map((h, index) => ({
      name: `#${index + 1}`,
      date: h.completed_at ? new Date(h.completed_at).toLocaleDateString('pt-BR') : '',
      score: h.overall_score,
      assertiveness: h.metrics.assertiveness,
      coherence: h.metrics.coherence,
      relevance: h.metrics.relevance,
      completeness: h.metrics.completeness,
      responseTime: h.avg_response_time_ms,
      testName: h.test_run_name,
    }));
  };

  const getLatestMetricsRadarData = () => {
    if (!testHistory?.history?.length) return [];
    const latest = testHistory.history[testHistory.history.length - 1];
    return [
      { metric: 'Assertividade', value: latest.metrics.assertiveness, fullMark: 100 },
      { metric: 'Coerência', value: latest.metrics.coherence, fullMark: 100 },
      { metric: 'Relevância', value: latest.metrics.relevance, fullMark: 100 },
      { metric: 'Completude', value: latest.metrics.completeness, fullMark: 100 },
    ];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!agent) {
    return (
      <Box>
        <Alert severity="error">Agente não encontrado</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/agents')} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/agents')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">{agent.name}</Typography>
            <Chip
              label={agent.is_active ? 'Ativo' : 'Inativo'}
              size="small"
              color={agent.is_active ? 'success' : 'default'}
            />
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
            onClick={handleTestConnection}
            disabled={testing}
          >
            Testar Conexão
          </Button>
          {editing ? (
            <>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                Salvar
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
            >
              Editar
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setTestResult(null)}
        >
          {testResult.message}
          {testResult.response_time_ms > 0 && (
            <Typography variant="caption" display="block">
              Tempo de resposta: {testResult.response_time_ms}ms
            </Typography>
          )}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Configurações" />
        <Tab label="Dashboard de Performance" />
      </Tabs>

      {/* Settings Tab */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="URL do Endpoint"
                fullWidth
                value={formData.endpoint_url}
                onChange={(e) => setFormData({ ...formData, endpoint_url: e.target.value })}
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrição"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="API Key"
                fullWidth
                type={editing ? 'text' : 'password'}
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome do Header da API Key"
                fullWidth
                value={formData.api_key_header}
                onChange={(e) => setFormData({ ...formData, api_key_header: e.target.value })}
                disabled={!editing}
                helperText="Ex: Authorization, x-api-key, Api-Key"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Response Path"
                fullWidth
                value={formData.response_path}
                onChange={(e) => setFormData({ ...formData, response_path: e.target.value })}
                disabled={!editing}
                helperText="JSONPath para extrair a resposta"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Payload Template (JSON)"
                fullWidth
                multiline
                rows={6}
                value={formData.payload_template}
                onChange={(e) => setFormData({ ...formData, payload_template: e.target.value })}
                disabled={!editing}
                helperText="Use {{question}} como placeholder para a pergunta"
                sx={{ '& textarea': { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Headers Adicionais (JSON)"
                fullWidth
                multiline
                rows={3}
                value={formData.headers}
                onChange={(e) => setFormData({ ...formData, headers: e.target.value })}
                disabled={!editing}
                sx={{ '& textarea': { fontFamily: 'monospace' } }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Informações
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Criado em: {new Date(agent.created_at).toLocaleString('pt-BR')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Atualizado em: {new Date(agent.updated_at).toLocaleString('pt-BR')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Dashboard Tab */}
      {tabValue === 1 && (
        <Box>
          {/* Filter Bar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <FilterIcon color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 1 }}>
                Filtrar por Teste:
              </Typography>
              <FormControl sx={{ minWidth: 350, maxWidth: 600 }} size="small">
                <InputLabel id="test-run-filter-label">Selecione os testes</InputLabel>
                <Select
                  labelId="test-run-filter-label"
                  multiple
                  value={selectedTestRunIds}
                  onChange={handleFilterChange}
                  input={<OutlinedInput label="Selecione os testes" />}
                  renderValue={(selected) => {
                    const names = allTestRuns
                      .filter((tr) => selected.includes(tr.id))
                      .map((tr) => tr.name);
                    return names.join(', ');
                  }}
                  disabled={loadingTestRuns}
                >
                  {allTestRuns.map((tr) => (
                    <MenuItem key={tr.id} value={tr.id}>
                      <Checkbox checked={selectedTestRunIds.indexOf(tr.id) > -1} />
                      <ListItemText
                        primary={tr.name}
                        secondary={`Score: ${tr.overall_score?.toFixed(1) || '-'}% | ${tr.completed_at ? new Date(tr.completed_at).toLocaleDateString('pt-BR') : ''}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedTestRunIds.length > 0 && (
                <Button size="small" onClick={handleClearFilter} variant="text">
                  Limpar filtro
                </Button>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {selectedTestRunIds.length > 0 && (
                <Chip
                  label={`${selectedTestRunIds.length} teste(s) selecionado(s)`}
                  color="primary"
                  size="small"
                />
              )}
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportPdf}
                disabled={!testHistory?.history?.length}
                size="small"
              >
                Exportar PDF
              </Button>
            </Box>
          </Paper>

          {loadingHistory ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : !testHistory?.history?.length ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                {selectedTestRunIds.length > 0
                  ? 'Nenhum resultado encontrado para os testes selecionados'
                  : 'Nenhum teste executado ainda'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedTestRunIds.length > 0
                  ? 'Tente alterar os filtros selecionados'
                  : 'Execute testes para visualizar o progresso do agente ao longo do tempo'}
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Summary Cards */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Total de Testes
                      </Typography>
                      <Typography variant="h4">
                        {testHistory.summary.total_test_runs}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Score Mais Recente
                      </Typography>
                      <Typography variant="h4">
                        {testHistory.summary.latest_score.toFixed(1)}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Melhor Score
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {testHistory.summary.best_score.toFixed(1)}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Tendência
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getTrendIcon(testHistory.summary.score_trend)}
                        <Typography variant="h6">
                          {getTrendLabel(testHistory.summary.score_trend)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Score Evolution Chart */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Evolução do Score ao Longo do Tempo
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={formatChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip 
                      formatter={(value, name) => {
                        const labels = {
                          score: 'Score Geral',
                          assertiveness: 'Assertividade',
                          coherence: 'Coerência',
                          relevance: 'Relevância',
                          completeness: 'Completude',
                        };
                        return [`${value.toFixed(1)}%`, labels[name] || name];
                      }}
                      labelFormatter={(label, payload) => {
                        if (payload && payload[0]) {
                          return `${payload[0].payload.testName} - ${label}`;
                        }
                        return label;
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#1976d2" 
                      strokeWidth={3}
                      name="Score Geral"
                      dot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>

              {/* Metrics Evolution Chart */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Evolução das Métricas
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={formatChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip 
                      formatter={(value, name) => {
                        const labels = {
                          assertiveness: 'Assertividade',
                          coherence: 'Coerência',
                          relevance: 'Relevância',
                          completeness: 'Completude',
                        };
                        return [`${value.toFixed(1)}%`, labels[name] || name];
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="assertiveness" stroke="#4caf50" name="Assertividade" />
                    <Line type="monotone" dataKey="coherence" stroke="#ff9800" name="Coerência" />
                    <Line type="monotone" dataKey="relevance" stroke="#9c27b0" name="Relevância" />
                    <Line type="monotone" dataKey="completeness" stroke="#f44336" name="Completude" />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>

              <Grid container spacing={3}>
                {/* Latest Metrics Radar Chart */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Métricas do Último Teste
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart data={getLatestMetricsRadarData()}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="metric" />
                        <PolarRadiusAxis domain={[0, 100]} />
                        <Radar
                          name="Score"
                          dataKey="value"
                          stroke="#1976d2"
                          fill="#1976d2"
                          fillOpacity={0.5}
                        />
                        <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Response Time Chart */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Tempo Médio de Resposta (ms)
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={formatChartData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip 
                          formatter={(value) => [`${value.toFixed(0)} ms`, 'Tempo de Resposta']}
                          labelFormatter={(label, payload) => {
                            if (payload && payload[0]) {
                              return `${payload[0].payload.testName} - ${label}`;
                            }
                            return label;
                          }}
                        />
                        <Bar dataKey="responseTime" fill="#1976d2" name="Tempo de Resposta" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>

              {/* Test History Table */}
              <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Histórico de Testes
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Teste</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Data</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Perguntas</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Score</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Assertividade</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Coerência</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Relevância</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Completude</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Tempo Médio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testHistory.history.map((h, index) => (
                        <tr key={h.test_run_id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '12px' }}>{h.test_run_name}</td>
                          <td style={{ padding: '12px' }}>
                            {h.completed_at ? new Date(h.completed_at).toLocaleDateString('pt-BR') : '-'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{h.total_questions}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Chip 
                              label={`${h.overall_score.toFixed(1)}%`}
                              color={h.overall_score >= 70 ? 'success' : h.overall_score >= 50 ? 'warning' : 'error'}
                              size="small"
                            />
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{h.metrics.assertiveness.toFixed(1)}%</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{h.metrics.coherence.toFixed(1)}%</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{h.metrics.relevance.toFixed(1)}%</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{h.metrics.completeness.toFixed(1)}%</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{h.avg_response_time_ms.toFixed(0)} ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Paper>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export default AgentDetail;
